import React, {
  useState,
  useRef,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { FlareContext } from "./flareContext";
import { FlareContextType } from "./types";
import {
  FuseArdServiceV2Client,
  GetFlareConfigCommand,
  GetFlareConfigCommandOutput,
  ListRagaAvailabilitiesCommand,
  ListRagaAvailabilitiesCommandOutput,
  RagaAvailability,
  FlareReleaseDomain,
} from "@amzn/fuse-ard-service-v2-client";
import { Ard } from "@amzn/fuse-accounting-central-datastore-client";
import { Auth } from "aws-amplify";
import { FUSE_ARD_SERVICE_V2_ENDPOINT } from "../../config/fuse-ard-service-v2-config";
import {
  ArdFlareComparison,
  compareArdWithFlareConfig,
} from "./flare-comparison-service";
import {
  CLIENT_CLEANUP_TIMEOUT_MS,
  CBOR_CONTENT_TYPE,
  AWS_REGION,
  ERROR_MESSAGES,
  FLARE_CONFIG_PATH_BATCH_SIZE,
} from "./constants";

interface FlareContextProviderProps {
  children: ReactNode;
}

export const FlareContextProvider: React.FC<FlareContextProviderProps> = ({
  children,
}) => {
  const [flareConfig, setFlareConfig] =
    useState<GetFlareConfigCommandOutput | null>(null);
  const [ragaAvailabilities, setRagaAvailabilities] = useState<
    RagaAvailability[] | null
  >(null);
  const [lastUpdatedOn, setLastUpdatedOn] = useState<Date | undefined>();
  const [loadingRagaAvailabilities, setLoadingRagaAvailabilities] =
    useState<boolean>(true);
  const [loadingFlareConfig, setLoadingFlareConfig] = useState<boolean>(true);
  const [loadingComparison, setLoadingComparison] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [ardFlareComparison, setArdFlareComparison] =
    useState<ArdFlareComparison | null>(null);

  // Use ref to store client instance to avoid recreating it on every render
  const clientRef = useRef<FuseArdServiceV2Client | null>(null);
  const clientTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const clientPromiseRef = useRef<Promise<FuseArdServiceV2Client> | null>(null);

  const getFuseArdServiceV2Client =
    useCallback(async (): Promise<FuseArdServiceV2Client> => {
      // Clear any existing timeout
      if (clientTimeoutRef.current) {
        clearTimeout(clientTimeoutRef.current);
      }

      // Return existing client if available
      if (clientRef.current) {
        // Reset timeout for client cleanup
        clientTimeoutRef.current = setTimeout(() => {
          clientRef.current = null;
        }, CLIENT_CLEANUP_TIMEOUT_MS);
        return clientRef.current;
      }

      // Return in-flight promise if creation is already happening
      if (clientPromiseRef.current) {
        return clientPromiseRef.current;
      }

      // Create promise for new client
      clientPromiseRef.current = (async () => {
        const credentials = await Auth.currentCredentials();
        const client = new FuseArdServiceV2Client({
          endpoint: FUSE_ARD_SERVICE_V2_ENDPOINT,
          region: AWS_REGION,
          credentials: Auth.essentialCredentials(credentials),
        });

        // Add the Accept header for CBOR transformation
        client.middlewareStack.add(
          (next: any) => (args: any) => {
            args.request.headers.accept = CBOR_CONTENT_TYPE;
            return next(args);
          },
          { name: "accept-cbor-header", override: true, step: "build" },
        );

        clientRef.current = client;
        clientPromiseRef.current = null; // Clear the promise

        // Set timeout to clean up client after 30 minutes
        clientTimeoutRef.current = setTimeout(() => {
          clientRef.current = null;
        }, CLIENT_CLEANUP_TIMEOUT_MS);

        return client;
      })();

      return clientPromiseRef.current;
    }, []);

  /**
   * Merges multiple GetFlareConfigCommandOutput responses into a single response.
   * Each response contains a `configs` field (string[]), where each string is a
   * full Flare tree for one domain. We concatenate all `configs` arrays across
   * batched responses so that parseFlareConfig sees all domain trees.
   */
  const mergeFlareConfigResponses = useCallback(
    (responses: GetFlareConfigCommandOutput[]): GetFlareConfigCommandOutput => {
      if (responses.length <= 1) {
        return responses[0] ?? ({} as GetFlareConfigCommandOutput);
      }
      return {
        ...responses[0],
        configs: responses.flatMap((r) => r.configs ?? []),
      };
    },
    [],
  );

  const getFlareConfig = useCallback(
    async (
      domains: FlareReleaseDomain[],
      paths: string[],
    ): Promise<GetFlareConfigCommandOutput | null> => {
      setLoadingFlareConfig(true);
      setLoadingComparison(true);
      setFlareConfig(null);
      setError(null);

      try {
        if (domains.length === 0 || paths.length === 0) {
          throw new Error("Domains and paths must not be empty");
        }

        const client = await getFuseArdServiceV2Client();

        // Batch paths to avoid 413 response size errors
        // Lambda has a 10MB response size limit, so we split paths into smaller batches
        const pathBatches: string[][] = [];
        for (let i = 0; i < paths.length; i += FLARE_CONFIG_PATH_BATCH_SIZE) {
          pathBatches.push(paths.slice(i, i + FLARE_CONFIG_PATH_BATCH_SIZE));
        }

        // Execute batched requests in parallel
        const batchPromises = pathBatches.map((pathBatch) => {
          const command = new GetFlareConfigCommand({
            domains,
            paths: pathBatch,
          });
          return client.send(command);
        });

        const batchResponses = await Promise.all(batchPromises);

        // Merge all batch responses into a single response
        const mergedResponse = mergeFlareConfigResponses(batchResponses);

        setFlareConfig(mergedResponse);
        return mergedResponse;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(errorMessage);
        console.error("Error fetching Flare config:", err);
        return null;
      } finally {
        setLoadingFlareConfig(false);
      }
    },
    [getFuseArdServiceV2Client, mergeFlareConfigResponses],
  );

  const listRagaAvailabilities = useCallback(
    async (
      forceFetchFromFlare: boolean = false,
    ): Promise<ListRagaAvailabilitiesCommandOutput | null> => {
      setLoadingRagaAvailabilities(true);
      setError(null);

      try {
        const client = await getFuseArdServiceV2Client();
        const command = new ListRagaAvailabilitiesCommand({
          forceFetchFromFlare,
        });
        const response = await client.send(command);

        if (!response.ragaAvailabilities) {
          throw new Error(ERROR_MESSAGES.MALFORMED_RESPONSE);
        }

        setRagaAvailabilities(response.ragaAvailabilities);
        setLastUpdatedOn(response.lastUpdatedOn);
        return response;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(errorMessage);
        console.error("Error fetching Raga availabilities:", err);
        return null;
      } finally {
        setLoadingRagaAvailabilities(false);
      }
    },
    [getFuseArdServiceV2Client],
  );

  const compareArdWithFlare = useCallback(
    (ard: Ard | null): void => {
      setArdFlareComparison(null); // Clear old comparison data immediately
      setError(null);

      if (!ard) {
        return;
      }
      setLoadingComparison(true);

      try {
        const comparison = compareArdWithFlareConfig(ard, flareConfig);

        setArdFlareComparison(comparison);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(errorMessage);
        console.error("Error comparing ARD with Flare config:", err);
      } finally {
        if (flareConfig) {
          setLoadingComparison(false);
        }
      }
    },
    [flareConfig],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (clientTimeoutRef.current) {
        clearTimeout(clientTimeoutRef.current);
      }
      clientRef.current = null;
    };
  }, []);

  const contextValue = useMemo<FlareContextType>(
    () => ({
      // State
      flareConfig,
      ragaAvailabilities,
      lastUpdatedOn,
      loadingRagaAvailabilities,
      loadingFlareConfig,
      loadingComparison,
      error,
      ardFlareComparison,
      // Actions
      getFlareConfig,
      listRagaAvailabilities,
      compareArdWithFlare,
      clearError,
    }),
    [
      flareConfig,
      ragaAvailabilities,
      lastUpdatedOn,
      loadingRagaAvailabilities,
      loadingFlareConfig,
      loadingComparison,
      error,
      ardFlareComparison,
      getFlareConfig,
      listRagaAvailabilities,
      compareArdWithFlare,
      clearError,
    ],
  );

  return (
    <FlareContext.Provider value={contextValue}>
      {children}
    </FlareContext.Provider>
  );
};
